import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) return setStatus("error");
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setStatus("paid");
        attempts += 1;
        if (attempts >= 12) return setStatus("timeout");
        setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= 12) return setStatus("error");
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-5 py-24 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <>
          <Loader2 size={44} className="animate-spin text-[#C5A059]" />
          <h1 className="mt-8 font-serif text-3xl font-semibold text-slate-50">Confirming your payment…</h1>
          <p className="mt-3 text-sm text-slate-400">Verifying with Stripe — this takes a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={52} className="text-emerald-400" data-testid="payment-confirmed-icon" />
          <h1 className="mt-8 font-serif text-4xl font-semibold text-slate-50" data-testid="payment-confirmed-title">Payment confirmed</h1>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
            Your order is with our HR desk. Complete your career questionnaire and upload your existing CV so we can begin immediately.
          </p>
          <Link to={`/intake?session_id=${sessionId}`} className="btn-gold mt-10" data-testid="payment-continue-intake-btn">
            Complete Your Questionnaire
          </Link>
        </>
      )}
      {(status === "timeout" || status === "error") && (
        <>
          <XCircle size={48} className="text-amber-400" />
          <h1 className="mt-8 font-serif text-3xl font-semibold text-slate-50" data-testid="payment-pending-title">Payment verification pending</h1>
          <p className="mt-3 max-w-sm text-sm text-slate-400">
            We couldn't confirm your payment yet — if you were charged, it will reconcile automatically. Check your client portal in a moment.
          </p>
          <Link to="/dashboard" className="btn-ghost mt-10" data-testid="payment-pending-dashboard-btn">Go to My Portal</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-5 py-24 text-center" data-testid="payment-cancel-page">
      <XCircle size={48} className="text-slate-500" />
      <h1 className="mt-8 font-serif text-3xl font-semibold text-slate-50">Checkout cancelled</h1>
      <p className="mt-3 max-w-sm text-sm text-slate-400">No charge was made. Your selected package is still waiting whenever you're ready.</p>
      <Link to="/order" className="btn-gold mt-10" data-testid="payment-cancel-retry-btn">Return to Checkout</Link>
    </div>
  );
}
